import React, { useMemo } from 'react';

import { StyleSheet, View } from 'react-native';

import {
  CONTENT_CONTAINER_PADDING_MOBILE,
  ListCardGroupContainer,
  ListHeader,
  NUM_COLUMNS,
  RenderItemRowContainer,
} from 'components/list-card-group/styled';
import { ListCardGroupVariant } from 'components/list-card-group/types';
import { formatData, groupItemsInRows } from 'components/list-card-group/utils';
import { Box } from 'components/ui';

interface StaticCardGridRenderItemParams<T> {
  item: T;
  index: number;
  rowIndex: number;
}

interface StaticCardGridProps<T> {
  items: T[];
  numColumns?: number;
  header?: string | React.ReactElement | null;
  listCardGroupVariant?: ListCardGroupVariant;
  isFullContainer?: boolean;
  renderItem: (params: StaticCardGridRenderItemParams<T>) => React.ReactNode;
}

export function StaticCardGrid<T>({
  items,
  numColumns = NUM_COLUMNS,
  header,
  listCardGroupVariant = ListCardGroupVariant.GROUP,
  isFullContainer = false,
  renderItem,
}: StaticCardGridProps<T>) {
  const rows = useMemo(() => groupItemsInRows(formatData([...items], numColumns), numColumns), [
    items,
    numColumns,
  ]);

  if (!rows.length) {
    return null;
  }

  const headerNode =
    typeof header === 'string' ? (
      <ListHeader listCardGroupVariant={listCardGroupVariant} style={styles.header}>
        {header}
      </ListHeader>
    ) : (
      header
    );

  return (
    <ListCardGroupContainer $isFullContainer={isFullContainer}>
      <View style={styles.container}>
        {headerNode}
        {rows.map((rowItems, rowIndex) => (
          <RenderItemRowContainer
            key={`static-card-row-${rowIndex}`}
            listCardGroupVariant={listCardGroupVariant}
          >
            {rowItems.map((item: T & { empty?: boolean }, index: number) => {
              if (item.empty) {
                return <Box key={`empty-${index}`} style={styles.emptyItem} />;
              }

              return renderItem({ item, index, rowIndex });
            })}
          </RenderItemRowContainer>
        ))}
      </View>
    </ListCardGroupContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: CONTENT_CONTAINER_PADDING_MOBILE,
    width: '100%',
  },
  header: {
    textTransform: 'capitalize',
  },
  emptyItem: {
    opacity: 0,
  },
});
